import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToastModule } from 'primeng/toast';
import { AvatarModule } from 'primeng/avatar';
import { MessageService } from 'primeng/api';
import { DashboardComponent } from '../../components/dashboard/dashboard.component';
import { DashboardConfig } from '../../components/dashboard/dashboard.types';
import { ConferenceService } from '../../services/conference.service';
import {
  ConferenceDto,
  ConferenceMetricsDto,
  ConnectionTimeSeriesDto,
  ConnectionCategoryDto,
  SessionDto,
  ConferenceLogDto,
} from '../../dto/conference.dto';
import {
  QueryResult,
  QueryResultItem,
} from '../../components/common-dto/query.dto';

@Component({
  selector: 'app-conference',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    CardModule,
    ProgressSpinnerModule,
    ToastModule,
    AvatarModule,
    DashboardComponent,
  ],
  providers: [MessageService],
  template: `
    <!-- Conference List View -->
    <div *ngIf="!currentConference" class="conference-list-container">
      <div class="conference-header">
        <h1>Conferences</h1>
        <p class="subtitle">Browse and join active and upcoming conferences</p>
      </div>

      <div *ngIf="loading" class="loading-container">
        <p-progressSpinner></p-progressSpinner>
        <p>Loading conferences...</p>
      </div>

      <div *ngIf="!loading && conferences.length === 0" class="empty-state">
        <i class="pi pi-calendar empty-icon"></i>
        <h3>No conferences available</h3>
        <p>There are currently no active or upcoming conferences.</p>
      </div>

      <div *ngIf="!loading && conferences.length > 0" class="conferences-grid">
        <p-card
          *ngFor="let conference of conferences"
          [class.active]="conference.item.isActive"
          [class.upcoming]="conference.item.isUpcoming"
          class="conference-card"
        >
          <ng-template pTemplate="header">
            <div class="conference-card-header">
              <span
                class="status-badge"
                [class.active]="conference.item.isActive"
              >
                {{ conference.item.isActive ? 'Active' : 'Upcoming' }}
              </span>
            </div>
          </ng-template>
          <div class="conference-card-content">
            <h2 class="conference-name">{{ conference.item.name }}</h2>
            <div class="conference-details">
              <div class="detail-item">
                <i class="pi pi-map-marker"></i>
                <span>{{ conference.item.location }}</span>
              </div>
              <div class="detail-item">
                <i class="pi pi-calendar"></i>
                <span>{{ formatDate(conference.item.date) }}</span>
              </div>
            </div>
            <div class="conference-actions" *ngIf="conference.item.isActive">
              <p-button
                label="Join"
                icon="pi pi-sign-in"
                (onClick)="joinConference(conference.id)"
                [loading]="joiningConferenceId === conference.id"
              ></p-button>
            </div>
          </div>
        </p-card>
      </div>

      <!-- Host a Conference Button -->
      <div class="host-conference-container">
        <p-button
          label="Host a Conference"
          icon="pi pi-plus"
          (onClick)="hostConference()"
          [outlined]="true"
          styleClass="host-button"
        ></p-button>
      </div>
    </div>

    <!-- Conference Detail View -->
    <div *ngIf="currentConference">
      <!-- Most Recent Log -->
      <div *ngIf="mostRecentLog" class="recent-log-container">
        <div class="recent-log-header">
          <h3>Latest Conference Log</h3>
          <p-button
            label="All"
            icon="pi pi-list"
            (onClick)="viewAllLogs()"
            [outlined]="true"
          ></p-button>
        </div>
        <p-card class="recent-log-card">
          <div class="log-content">
            <div class="log-header">
              <div class="log-user">
                <p-avatar
                  [image]="mostRecentLog.user.avatar"
                  [label]="
                    mostRecentLog.user.initials ||
                    getInitials(mostRecentLog.user.name)
                  "
                  shape="circle"
                  size="normal"
                ></p-avatar>
                <div class="user-info">
                  <div class="user-name">{{ mostRecentLog.user.name }}</div>
                  <div class="log-date">
                    {{ formatDateTime(mostRecentLog.dateTime) }}
                  </div>
                </div>
              </div>
            </div>
            <h4 class="log-title">{{ mostRecentLog.title }}</h4>
            <p class="log-note">{{ mostRecentLog.note }}</p>
            <div *ngIf="mostRecentLog.imageUrl" class="log-image">
              <img [src]="mostRecentLog.imageUrl" [alt]="mostRecentLog.title" />
            </div>
          </div>
        </p-card>
      </div>
      <pb-dashboard #dashboard [config]="dashboardConfig"></pb-dashboard>
      <div class="leave-button-container">
        <p-button
          label="Leave Conference"
          icon="pi pi-sign-out"
          severity="danger"
          (onClick)="leaveConference()"
          [loading]="leaving"
        ></p-button>
      </div>
    </div>

    <p-toast></p-toast>
  `,
  styles: [
    `
      .conference-list-container {
        width: 100%;
        max-width: 1200px;
        margin: 0 auto;
        padding: 2rem;
      }

      .conference-header {
        margin-bottom: 2rem;
      }

      .conference-header h1 {
        font-size: 2rem;
        font-weight: 600;
        margin: 0 0 0.5rem 0;
        color: var(--text-color);
      }

      .subtitle {
        color: var(--text-color-secondary);
        margin: 0;
      }

      .loading-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 3rem;
        gap: 1rem;
      }

      .empty-state {
        text-align: center;
        padding: 4rem 2rem;
      }

      .empty-icon {
        font-size: 4rem;
        color: var(--text-color-secondary);
        margin-bottom: 1rem;
      }

      .empty-state h3 {
        font-size: 1.5rem;
        margin: 0 0 0.5rem 0;
      }

      .empty-state p {
        color: var(--text-color-secondary);
      }

      .conferences-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
        gap: 1.5rem;
      }

      .conference-card {
        transition: transform 0.2s, box-shadow 0.2s;
      }

      .conference-card:hover {
        transform: translateY(-4px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      }

      .conference-card.active {
        border-left: 4px solid #4caf50;
      }

      .conference-card.upcoming {
        border-left: 4px solid #2196f3;
      }

      .conference-card-header {
        padding: 1rem;
        display: flex;
        justify-content: flex-end;
      }

      .status-badge {
        padding: 0.25rem 0.75rem;
        border-radius: 12px;
        font-size: 0.75rem;
        font-weight: 600;
        background-color: var(--surface-ground);
        color: var(--text-color);
      }

      .status-badge.active {
        background-color: #4caf50;
        color: white;
      }

      .conference-card-content {
        padding: 1rem;
      }

      .conference-name {
        font-size: 1.5rem;
        font-weight: 600;
        margin: 0 0 1rem 0;
        color: var(--text-color);
      }

      .conference-details {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
        margin-bottom: 1.5rem;
      }

      .detail-item {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        color: var(--text-color-secondary);
      }

      .detail-item i {
        color: var(--primary-color);
      }

      .conference-actions {
        display: flex;
        justify-content: flex-end;
      }

      .leave-button-container {
        display: flex;
        justify-content: center;
        padding: 2rem;
        margin-top: 2rem;
      }

      .host-conference-container {
        display: flex;
        justify-content: center;
        padding: 2rem 0;
        margin-top: 2rem;
      }

      :host ::ng-deep .host-button {
        font-size: 1.1rem;
        padding: 0.75rem 2rem;
      }

      .recent-log-container {
        margin-bottom: 2rem;
        padding: 0 1rem;
      }

      .recent-log-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 1rem;
      }

      .recent-log-header h3 {
        margin: 0;
        font-size: 1.5rem;
        font-weight: 600;
        color: var(--text-color);
      }

      .recent-log-card {
        margin-bottom: 0;
      }

      .log-content {
        padding: 0;
      }

      .log-header {
        margin-bottom: 1rem;
      }

      .log-user {
        display: flex;
        align-items: center;
        gap: 0.75rem;
      }

      .user-info {
        display: flex;
        flex-direction: column;
      }

      .user-name {
        font-weight: 600;
        font-size: 0.95rem;
        color: var(--text-color);
        margin-bottom: 0.25rem;
      }

      .log-date {
        font-size: 0.85rem;
        color: var(--text-color-secondary);
      }

      .log-title {
        font-size: 1.25rem;
        font-weight: 600;
        margin: 0 0 0.75rem 0;
        color: var(--text-color);
      }

      .log-note {
        font-size: 1rem;
        line-height: 1.6;
        color: var(--text-color);
        margin: 0 0 1rem 0;
        white-space: pre-wrap;
      }

      .log-image {
        margin-top: 1rem;
        border-radius: 8px;
        overflow: hidden;
      }

      .log-image img {
        width: 100%;
        height: auto;
        display: block;
        max-height: 400px;
        object-fit: cover;
      }

      :host ::ng-deep .p-avatar {
        width: 40px;
        height: 40px;
      }

      @media screen and (max-width: 768px) {
        .conference-list-container {
          padding: 1rem;
        }

        .conferences-grid {
          grid-template-columns: 1fr;
        }
      }
    `,
  ],
})
export class ConferenceComponent implements OnInit {
  @ViewChild('dashboard') dashboard!: DashboardComponent;

  conferences: QueryResultItem<ConferenceDto>[] = [];
  currentConference: ConferenceDto | null = null;
  currentConferenceId: string | null = null;
  loading = false;
  joiningConferenceId: string | null = null;
  leaving = false;
  mostRecentLog: ConferenceLogDto | null = null;
  dashboardConfig: DashboardConfig = {
    header: 'Conference',
    items: [],
  };

  constructor(
    private conferenceService: ConferenceService,
    private messageService: MessageService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadConferences();
    this.checkCurrentConference();
  }

  checkCurrentConference(): void {
    // Check if we're using MockConferenceService and have a current conference
    if ('getCurrentConferenceId' in this.conferenceService) {
      const conferenceId = (
        this.conferenceService as any
      ).getCurrentConferenceId();
      if (conferenceId) {
        this.loadConferenceDetail(conferenceId);
      }
    }
  }

  loadConferences(): void {
    this.loading = true;
    this.conferenceService.getConferences({ take: 100, skip: 0 }).subscribe({
      next: (result: QueryResult<ConferenceDto>) => {
        this.conferences = result.items;
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading conferences:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load conferences',
        });
        this.loading = false;
      },
    });
  }

  joinConference(conferenceId: string): void {
    this.joiningConferenceId = conferenceId;
    this.conferenceService.joinConference(conferenceId).subscribe({
      next: (result) => {
        if (result.success) {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: result.message || 'Successfully joined conference',
          });
          this.loadConferenceDetail(conferenceId);
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: result.message || 'Failed to join conference',
          });
        }
        this.joiningConferenceId = null;
      },
      error: (error) => {
        console.error('Error joining conference:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to join conference',
        });
        this.joiningConferenceId = null;
      },
    });
  }

  leaveConference(): void {
    if (!this.currentConferenceId) return;

    this.leaving = true;
    this.conferenceService.leaveConference(this.currentConferenceId).subscribe({
      next: (result) => {
        if (result.success) {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: result.message || 'Successfully left conference',
          });
          this.currentConference = null;
          this.currentConferenceId = null;
          this.loadConferences();
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: result.message || 'Failed to leave conference',
          });
        }
        this.leaving = false;
      },
      error: (error) => {
        console.error('Error leaving conference:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to leave conference',
        });
        this.leaving = false;
      },
    });
  }

  loadConferenceDetail(conferenceId: string): void {
    this.currentConferenceId = conferenceId;
    this.conferenceService.getConference(conferenceId).subscribe({
      next: (conference: ConferenceDto) => {
        this.currentConference = conference;
        this.setupDashboard(conferenceId);
        this.loadMostRecentLog(conferenceId);
      },
      error: (error) => {
        console.error('Error loading conference details:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load conference details',
        });
      },
    });
  }

  loadMostRecentLog(conferenceId: string): void {
    this.conferenceService.getMostRecentLog(conferenceId).subscribe({
      next: (log: ConferenceLogDto | null) => {
        this.mostRecentLog = log;
      },
      error: (error) => {
        console.error('Error loading most recent log:', error);
        // Don't show error toast for logs, just fail silently
      },
    });
  }

  viewAllLogs(): void {
    if (this.currentConferenceId) {
      this.router.navigate(['/conference', this.currentConferenceId, 'logs']);
    }
  }

  getInitials(name: string): string {
    if (!name) return '';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }

  formatDateTime(date: Date): string {
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  hostConference(): void {
    this.router.navigate(['/conference', 'new']);
  }

  setupDashboard(conferenceId: string): void {
    if (!this.currentConference) return;

    this.dashboardConfig = {
      header: this.currentConference.name,
      subheader: `${this.currentConference.location} • ${this.formatDate(
        this.currentConference.date
      )}`,
      items: [
        // Metrics Cards
        {
          type: 'card',
          colSpan: 3,
          data: {
            title: 'Attendees',
            value: '0',
            icon: 'pi pi-users',
            backgroundColor: '#42A5F5',
            color: '#FFFFFF',
            onClick: () => {
              this.router.navigate(['/conference', conferenceId, 'attendees']);
            },
          },
          loadItems: () =>
            this.conferenceService.getConferenceMetrics(conferenceId),
          formatData: (item, data) => {
            item.value = data.numberOfAttendees || 0;
            return item.value;
          },
        },
        {
          type: 'card',
          colSpan: 3,
          data: {
            title: 'Vendors',
            value: '0',
            icon: 'pi pi-building',
            backgroundColor: '#66BB6A',
            color: '#FFFFFF',
            onClick: () => {
              this.router.navigate(['/conference', conferenceId, 'vendors']);
            },
          },
          loadItems: () =>
            this.conferenceService.getConferenceMetrics(conferenceId),
          formatData: (item, data) => {
            item.value = data.numberOfVendors || 0;
            return item.value;
          },
        },
        {
          type: 'card',
          colSpan: 3,
          data: {
            title: 'Connections',
            value: '0',
            icon: 'pi pi-link',
            backgroundColor: '#FFA726',
            color: '#FFFFFF',
            onClick: () => {
              this.router.navigate([
                '/conference',
                conferenceId,
                'connections',
              ]);
            },
          },
          loadItems: () =>
            this.conferenceService.getConferenceMetrics(conferenceId),
          formatData: (item, data) => {
            item.value = data.numberOfConnections || 0;
            return item.value;
          },
        },
        {
          type: 'card',
          colSpan: 3,
          data: {
            title: 'Sessions',
            value: '0',
            icon: 'pi pi-calendar-times',
            backgroundColor: '#AB47BC',
            color: '#FFFFFF',
            onClick: () => {
              this.router.navigate(['/conference', conferenceId, 'sessions']);
            },
          },
          loadItems: () =>
            this.conferenceService.getSessions(conferenceId, {
              take: 1,
              skip: 0,
            }),
          formatData: (item, data) => {
            item.value = data.total || 0;
            return item.value;
          },
        },
        // Connections Over Time Chart
        {
          type: 'chart',
          colSpan: 12,
          data: {
            title: 'Connections Over Time',
            type: 'line',
            height: '400px',
            data: {
              labels: [],
              datasets: [
                {
                  label: 'Connections',
                  data: [],
                  borderColor: '#42A5F5',
                  backgroundColor: 'rgba(66, 165, 245, 0.1)',
                  tension: 0.4,
                },
              ],
            },
            options: {
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: {
                  display: true,
                  position: 'top',
                },
              },
              scales: {
                y: {
                  beginAtZero: true,
                  title: {
                    display: true,
                    text: 'Number of Connections',
                  },
                },
                x: {
                  title: {
                    display: true,
                    text: 'Time',
                  },
                },
              },
            },
          },
          loadItems: () =>
            this.conferenceService.getConnectionsOverTime(conferenceId),
          formatData: (item, data: ConnectionTimeSeriesDto[]) => {
            return {
              labels: data.map((d) => d.date),
              datasets: [
                {
                  label: 'Connections',
                  data: data.map((d) => d.connections),
                  borderColor: '#42A5F5',
                  backgroundColor: 'rgba(66, 165, 245, 0.1)',
                  tension: 0.4,
                },
              ],
            };
          },
        },
        // Connections By Category Chart
        {
          type: 'chart',
          colSpan: 12,
          data: {
            title: 'Connections by Category',
            type: 'bar',
            height: '400px',
            data: {
              labels: [],
              datasets: [
                {
                  label: 'Connections',
                  data: [],
                  borderColor: '#66BB6A',
                  backgroundColor: '#66BB6A',
                },
              ],
            },
            options: {
              responsive: true,
              maintainAspectRatio: false,
              plugins: {
                legend: {
                  display: true,
                  position: 'top',
                },
              },
              scales: {
                y: {
                  beginAtZero: true,
                  title: {
                    display: true,
                    text: 'Number of Connections',
                  },
                },
                x: {
                  title: {
                    display: true,
                    text: 'Category',
                  },
                },
              },
            },
          },
          loadItems: () =>
            this.conferenceService.getConnectionsByCategory(conferenceId),
          formatData: (item, data: ConnectionCategoryDto[]) => {
            return {
              labels: data.map((d) => d.category),
              datasets: [
                {
                  label: 'Connections',
                  data: data.map((d) => d.connections),
                  borderColor: '#66BB6A',
                  backgroundColor: '#66BB6A',
                },
              ],
            };
          },
        },
      ],
    };
  }

  formatDate(date: Date): string {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }
}
